import type {
  FacetContext,
  FacetFilterField,
  GameDefinition,
  GameFacetDefinition,
  GameId,
} from "modules/common/model/models";

export type {
  FacetContext,
  FacetFilterField,
  FacetControl,
  FacetFieldKind,
  FacetPresentation,
  FacetPurpose,
} from "modules/common/model/models";

export type CardFacetRegistration = GameFacetDefinition;

const FACET_CONTROLS = new Set([
  "game-pills",
  "group",
  "searchable-group",
  "range-input",
  "hybrid-range",
]);
const FACET_FIELD_KINDS = new Set([
  "string-array",
  "integer-array",
  "integer",
  "float",
  "match-mode",
]);
const FACET_CONTEXTS = new Set(["collection", "cards", "trade"]);
const FACET_PURPOSES = new Set(["game-scope", "list-status"]);
const FACET_PRESENTATIONS = new Set([
  "plain",
  "condition",
  "mana-color",
  "domain",
  "card-type",
]);

function normalizeFilterField(value: unknown): FacetFilterField | null {
  if (!value || typeof value !== "object") return null;
  const field = value as Partial<FacetFilterField>;
  if (
    typeof field.key !== "string" ||
    !field.key ||
    typeof field.queryKey !== "string" ||
    !field.queryKey ||
    typeof field.kind !== "string" ||
    !FACET_FIELD_KINDS.has(field.kind)
  ) {
    return null;
  }
  return field as FacetFilterField;
}

export function normalizeGameFacetDefinitions(
  value: unknown
): CardFacetRegistration[] | null {
  if (!Array.isArray(value)) return null;
  const result: CardFacetRegistration[] = [];
  for (const candidate of value) {
    if (!candidate || typeof candidate !== "object") return null;
    const facet = candidate as Partial<CardFacetRegistration>;
    if (
      typeof facet.key !== "string" ||
      !facet.key ||
      typeof facet.title !== "string" ||
      !facet.title ||
      typeof facet.facetKey !== "string" ||
      !facet.facetKey ||
      typeof facet.control !== "string" ||
      !FACET_CONTROLS.has(facet.control)
    ) {
      return null;
    }
    const selection =
      facet.selection === undefined
        ? undefined
        : normalizeFilterField(facet.selection);
    const matchMode =
      facet.matchMode === undefined
        ? undefined
        : normalizeFilterField(facet.matchMode);
    if (selection === null || matchMode === null) return null;
    if (matchMode && matchMode.kind !== "match-mode") return null;

    let range: CardFacetRegistration["range"];
    if (facet.range !== undefined) {
      if (!facet.range || typeof facet.range !== "object") return null;
      const min = normalizeFilterField(facet.range.min);
      const max = normalizeFilterField(facet.range.max);
      if (
        !min ||
        !max ||
        min.kind !== max.kind ||
        (min.kind !== "integer" && min.kind !== "float") ||
        (facet.range.step !== undefined &&
          (typeof facet.range.step !== "number" ||
            !Number.isFinite(facet.range.step)))
      ) {
        return null;
      }
      range = {
        min,
        max,
        ...(typeof facet.range.step === "number"
          ? { step: facet.range.step }
          : {}),
        ...(facet.range.isCurrency === true ? { isCurrency: true } : {}),
      };
    }

    let contexts: FacetContext[] | undefined;
    if (facet.contexts !== undefined) {
      if (!Array.isArray(facet.contexts)) return null;
      contexts = facet.contexts.filter(
        (context): context is FacetContext =>
          typeof context === "string" && FACET_CONTEXTS.has(context)
      );
      if (contexts.length !== facet.contexts.length) return null;
    }
    if (
      facet.purpose !== undefined &&
      (typeof facet.purpose !== "string" || !FACET_PURPOSES.has(facet.purpose))
    ) {
      return null;
    }
    if (
      facet.presentation !== undefined &&
      !FACET_PRESENTATIONS.has(facet.presentation)
    ) {
      return null;
    }
    if (
      facet.exclusiveValue !== undefined &&
      typeof facet.exclusiveValue !== "string" &&
      typeof facet.exclusiveValue !== "number"
    ) {
      return null;
    }
    if (
      facet.requiresMultipleGames !== undefined &&
      typeof facet.requiresMultipleGames !== "boolean"
    ) {
      return null;
    }
    if (!selection && !range) return null;
    switch (facet.control) {
      case "group":
      case "searchable-group":
        if (!selection || range) return null;
        break;
      case "game-pills":
        if (
          !selection ||
          selection.kind !== "string-array" ||
          range ||
          facet.requiresMultipleGames !== true
        ) {
          return null;
        }
        break;
      case "range-input":
        if (selection || !range) return null;
        break;
      case "hybrid-range":
        if (!selection || !range) return null;
        break;
    }

    result.push({
      key: facet.key,
      title: facet.title,
      facetKey: facet.facetKey,
      control: facet.control,
      ...(facet.purpose ? { purpose: facet.purpose } : {}),
      ...(contexts?.length ? { contexts } : {}),
      ...(selection ? { selection } : {}),
      ...(range ? { range } : {}),
      ...(matchMode ? { matchMode } : {}),
      ...(facet.presentation ? { presentation: facet.presentation } : {}),
      ...(facet.exclusiveValue !== undefined
        ? { exclusiveValue: facet.exclusiveValue }
        : {}),
      ...(facet.requiresMultipleGames === true
        ? { requiresMultipleGames: true }
        : {}),
    });
  }
  return result;
}

export type FacetFilterValue =
  | string
  | string[]
  | number[]
  | number
  | boolean
  | "and"
  | "or"
  | "exact"
  | undefined;

export type FacetFilterState = Record<string, FacetFilterValue>;

const ALL_CONTEXTS = ["collection", "cards", "trade"] as const;

function supportsContext(
  registration: CardFacetRegistration,
  context: FacetContext
): boolean {
  return (registration.contexts ?? ALL_CONTEXTS).includes(context);
}

export interface RegisteredCardFacet extends CardFacetRegistration {
  gameExclusive?: GameId;
}

export interface FacetContract {
  definitions: ReadonlyArray<Pick<GameDefinition, "id" | "facets">>;
  sharedFacets: readonly CardFacetRegistration[];
}

export function registeredCardFacets(
  contract: FacetContract,
  context?: FacetContext
): RegisteredCardFacet[] {
  const shared = contract.sharedFacets.filter(
    (registration) => !context || supportsContext(registration, context)
  );
  const gameEntries = contract.definitions.map(
    (definition) => [definition.id, definition.facets] as const
  );
  const gameFacets = gameEntries.flatMap(([gameId, facets]) =>
    facets
      .filter((facet) => !context || supportsContext(facet, context))
      .map((facet) => ({ ...facet, gameExclusive: gameId }))
  );
  return [...shared, ...gameFacets];
}

export function registeredFacetFields(
  contract: FacetContract,
  context?: FacetContext
): FacetFilterField[] {
  const fields = new Map<string, FacetFilterField>();
  for (const facet of registeredCardFacets(contract, context)) {
    const candidates = [
      facet.selection,
      facet.range?.min,
      facet.range?.max,
      facet.matchMode,
    ];
    for (const field of candidates) {
      if (!field) continue;
      const existing = fields.get(field.key);
      if (
        existing &&
        (existing.queryKey !== field.queryKey || existing.kind !== field.kind)
      ) {
        throw new Error(
          `Conflicting facet field registration for ${field.key}`
        );
      }
      fields.set(field.key, field);
    }
  }
  return [...fields.values()];
}

/** The game-scope field is identified by its generic control contract, never
 * by a frontend-owned field or query-key spelling. */
export function gameFacetField(
  contract: FacetContract,
  context?: FacetContext
): FacetFilterField {
  const facet = registeredCardFacets(contract, context).find(
    (registration) => registration.purpose === "game-scope"
  );
  if (!facet?.selection) {
    throw new Error("Facet contract is missing its game-scope selection");
  }
  return facet.selection;
}

export function gameFacetValues(
  source: Record<string, unknown>,
  contract: FacetContract,
  context?: FacetContext
): string[] {
  const value = source[gameFacetField(contract, context).key];
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

export function withDefaultGameFacet<T extends Record<string, unknown>>(
  source: T,
  defaults: readonly GameId[] | undefined,
  contract: FacetContract,
  context?: FacetContext
): T {
  if (!defaults?.length || gameFacetValues(source, contract, context).length) {
    return source;
  }
  const field = gameFacetField(contract, context);
  return { ...source, [field.key]: [...defaults] };
}

function collectionStatusFacetField(contract: FacetContract): FacetFilterField {
  const facet = registeredCardFacets(contract, "collection").find(
    (registration) => registration.purpose === "list-status"
  );
  if (!facet?.selection) {
    throw new Error("Facet contract is missing its list-status selection");
  }
  return facet.selection;
}

export function withCollectionStatusFacet<T extends Record<string, unknown>>(
  source: T,
  statuses: readonly string[],
  contract: FacetContract
): T {
  const field = collectionStatusFacetField(contract);
  return { ...source, [field.key]: [...statuses] };
}

export function normalizeFacetState(
  source: Record<string, unknown>,
  contract: FacetContract,
  context?: FacetContext
): FacetFilterState {
  const result: FacetFilterState = {};
  for (const field of registeredFacetFields(contract, context)) {
    const value = source[field.key];
    if (field.kind.endsWith("-array")) {
      if (Array.isArray(value) && value.length > 0) {
        result[field.key] = value as string[] | number[];
      }
      continue;
    }
    if (field.kind === "match-mode") {
      if (value === "and" || value === "exact") result[field.key] = value;
      continue;
    }
    if (typeof value === "number" && value !== 0) result[field.key] = value;
  }
  return result;
}

export function hasActiveFacetState(
  source: Record<string, unknown>,
  contract: FacetContract,
  context?: FacetContext
): boolean {
  return registeredFacetFields(contract, context).some((field) => {
    if (field.kind === "match-mode") return false;
    const value = source[field.key];
    return Array.isArray(value)
      ? value.length > 0
      : typeof value === "number" && value !== 0;
  });
}

export function emptyFacetState(
  contract: FacetContract,
  context?: FacetContext
): FacetFilterState {
  const result: FacetFilterState = {};
  for (const field of registeredFacetFields(contract, context)) {
    if (field.kind.endsWith("-array")) result[field.key] = [];
    else if (field.kind === "match-mode") result[field.key] = "or";
    else result[field.key] = 0;
  }
  return result;
}

export function appendFacetQueryParams(
  params: URLSearchParams,
  source: Record<string, unknown>,
  contract: FacetContract,
  context?: FacetContext
): URLSearchParams {
  for (const field of registeredFacetFields(contract, context)) {
    const value = source[field.key];
    if (Array.isArray(value)) {
      for (const entry of value) params.append(field.queryKey, String(entry));
      continue;
    }
    if (field.kind === "match-mode") {
      if (value === "and" || value === "exact") {
        params.set(field.queryKey, value);
      }
      continue;
    }
    if (typeof value === "number" && value !== 0) {
      params.set(field.queryKey, String(value));
    }
  }
  return params;
}

export function validateFacetContracts(contract: FacetContract): void {
  const facetKeys = new Map<string, string>();
  const queryKeys = new Map<string, string>();
  const purposes = new Map<string, string>();
  for (const facet of registeredCardFacets(contract)) {
    const owner = facet.gameExclusive ?? "shared";
    const existing = facetKeys.get(facet.key);
    if (existing) {
      throw new Error(
        `Facet ${facet.key} is registered by ${existing} and ${owner}`
      );
    }
    facetKeys.set(facet.key, owner);
    if (facet.purpose) {
      const purposeOwner = purposes.get(facet.purpose);
      if (purposeOwner) {
        throw new Error(
          `Facet purpose ${facet.purpose} is registered by ${purposeOwner} and ${owner}`
        );
      }
      purposes.set(facet.purpose, owner);
    }
  }
  for (const field of registeredFacetFields(contract)) {
    const existing = queryKeys.get(field.queryKey);
    if (existing && existing !== field.key) {
      throw new Error(
        `Facet query key ${field.queryKey} is registered by ${existing} and ${field.key}`
      );
    }
    queryKeys.set(field.queryKey, field.key);
  }
  for (const purpose of ["game-scope", "list-status"] as const) {
    if (!purposes.has(purpose)) {
      throw new Error(`Facet contract is missing required purpose ${purpose}`);
    }
  }
}

export function normalizeFacetResponseKeys<T>(
  facets: Record<string, T>
): Record<string, T> {
  const result: Record<string, T> = {};
  for (const [key, value] of Object.entries(facets)) {
    result[
      key.replace(/_([a-z])/g, (_, character: string) =>
        character.toUpperCase()
      )
    ] = value;
  }
  return result;
}
